import React, { useState, useEffect, useCallback } from 'react';
import { Map, Loader2, Package } from 'lucide-react';
import { projectService } from '../services/projectService';
import { formatInstallationTrade } from '../../verve-teams/utils/teamMemberLabels';

export const ProjectSiteInventoryTab = ({ project }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!project?._id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await projectService.getSiteInventory(project._id);
      setItems(data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not load site inventory');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [project?._id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-12 flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-8 h-8 text-[#2b448f] animate-spin" />
        <span className="text-[11px] font-semibold text-slate-400">Loading site inventory…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-rose-100 bg-rose-50/40 p-6 text-[13px] font-semibold text-rose-700">
        {error}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col">
      <div className="p-6 border-b border-slate-100 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center border border-slate-100">
          <Map className="w-5 h-5 text-[#2b448f]" />
        </div>
        <div>
          <h2 className="text-[15px] font-bold text-slate-800 tracking-tight">Site inventory</h2>
          <p className="text-[11px] font-semibold text-slate-400 mt-0.5">
            Materials confirmed received on site for this project
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="p-12 flex flex-col items-center justify-center gap-3 text-center">
          <Package className="w-10 h-10 text-slate-200" />
          <p className="text-[13px] font-semibold text-slate-500 max-w-md">
            No materials recorded on site yet. They appear here after installation members mark dispatched lines as
            received.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50 text-[13px] font-bold uppercase tracking-tight text-slate-400">
                <th className="px-6 py-3">Material</th>
                <th className="px-6 py-3">Qty</th>
                <th className="px-6 py-3">Request</th>
                <th className="px-6 py-3">Team</th>
                <th className="px-6 py-3">Installation member</th>
                <th className="px-6 py-3">Received</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={`${row.materialRequestId}-${row.itemIndex}`} className="border-b border-slate-50 hover:bg-slate-50/40">
                  <td className="px-6 py-3 font-bold text-slate-800">{row.itemName}</td>
                  <td className="px-6 py-3 font-semibold text-slate-600 tabular-nums">{row.quantity}</td>
                  <td className="px-6 py-3 font-semibold text-slate-500">{row.requestNo || '—'}</td>
                  <td className="px-6 py-3 font-semibold text-slate-600 capitalize">
                    {row.installationTrade ? formatInstallationTrade(row.installationTrade) : '—'}
                  </td>
                  <td className="px-6 py-3 font-semibold text-slate-600">{row.assignedOfficerName || '—'}</td>
                  <td className="px-6 py-3 font-semibold text-slate-500 whitespace-nowrap">
                    {row.siteReceivedAt
                      ? new Date(row.siteReceivedAt).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
